<template>
  <div>
    <div slot="title" class="mb-5">
      <h4>
        {{ $t('people_manager.patient.checkup_appointments_list_title')}}
      </h4>
    </div>
    <v-server-table
      :columns="tableColumns"
      :options="tableOptions"
      @row-click="click => handleRowClick(click.row)"
      id="patientCheckupAppointments"
      ref="patientCheckupAppointments"
      name="patientCheckupAppointments">

      <span slot="start" slot-scope="{row}">
        <span v-if="row.begin">
          {{ row.begin | localTime('YYYY-MM-DD HH:mm') }}
        </span>
      </span>
      <span slot="end" slot-scope="{row}">
        <span v-if="row.begin">
          {{ row.end | localTime('YYYY-MM-DD HH:mm') }}
        </span>
      </span>
      <span slot="status" slot-scope="{row}">
        <span v-if="row.status">
          {{ $t(`checkup_plus.appointments.statuses.${row.status}`) }}
        </span>
        <span v-else>
          "N/A"
        </span>
      </span>
      <span slot="tests" slot-scope="{row}">
        <span v-if="row.measurementAggregate && row.measurementAggregate.length > 0">
          <span v-for="id in distinctMeasurementGroupsInAppointment(row.measurementAggregate)">
            <va-popover
              class="ml-1"
              placement="top"
              :message="$t(`checkup_plus.person.rules.rule_creation.measurement_group.${id}`)">
            <i :class="handleIconStr(id)" style="height: 30px; width: 30px; filter: invert(71%) sepia(60%) saturate(1%) hue-rotate(350deg) brightness(99%) contrast(90%);" ></i>
          </va-popover>
          </span>
        </span>
        <span v-else>
          ---
        </span>

      </span>
      <span slot="facility" slot-scope="{row}">
        <span v-if="allowedFacilities.length > 0 && allowedFacilities.some(facility => facility.id === row.facilityId)">
          {{ allowedFacilities.find(facility => facility.id === row.facilityId).name }}
        </span>
        <span v-else>
          "N/A"
        </span>
      </span>

    </v-server-table>

    <MeasurementDetailsModal
      v-if="selectedAppointment"
      :measurements="selectedAppointment.measurementAggregate ? selectedAppointment.measurementAggregate : []"
      :patientName="handlePatientName()"
      :appointmentFrom="selectedAppointment.begin | localTime('YYYY-MM-DD HH:mm')"
      :appointmentTo="selectedAppointment.end | localTime('YYYY-MM-DD HH:mm')"
      @cancel="handleMeasurementDetailsModalClose"
    />

  </div>

</template>

<script>
import TableQueryMixin from '@/mixins/TableQueryMixin.vue';
import { OneCareService } from '@/api/OneCareService';
import ApiService from '@/api/ApiService';
import Vue from "vue";
import axios from "axios";
import { mapState } from 'vuex';
import MeasurementDetailsModal from '@/components/people-manager/patient/MeasurementDetailsModal.vue';

export default {
  components: { MeasurementDetailsModal },
  props: {
    patient: {
      type: Object,
      required: true
    },
    allowedEntities: {
      type: Array,
      required: true
    }
  },
  mixins: [
    TableQueryMixin
  ],
  data: function () {
    return {
      selectedAppointment: null,
      tableOptions:{
        filterable: false,
        saveState: false,
        columnsDropdown: false,
        sortable: [],
        requestFunction: (data) => {
          const page = data.page
          return new Promise((resolve, reject) => {
            if(this.cancelationCallback !== null) {
              this.cancelationCallback()
            }
            this.isLoading = true
            const limit = this.$refs.patientCheckupAppointments ? this.$refs.patientCheckupAppointments.limit : 10
            const queryUrl = this.queryUrl+`&pageSize=${limit}`

            ApiService.cancelableQuery(queryUrl, data, new axios.CancelToken(c => {
              this.cancelationCallback = c
            }))
              .then(response => {
                if (response.data.page && this.$refs.patientCheckupAppointments) {
                  this.$refs.patientCheckupAppointments.$refs.pagination.Page = response.data.page
                } else if(page && this.$refs.patientCheckupAppointments) {
                  this.$refs.patientCheckupAppointments.$refs.pagination.Page = page
                }
                resolve({
                  data: response.data.objects ? response.data.objects : [],
                  count: response.data.totalItems ? response.data.totalItems : 0,
                  page: response.data.page ? response.data.page : 1
                })
              })
              .catch(error => {
                if (!axios.isCancel(error)) {
                  reject(error)
                }

                if(error.response.status === 404) {
                  if(error.response.data.error_description.includes('Could\'t find Person patient with id:')) {
                    this.isLoading = false
                    this.$refs.patientCheckupAppointments.loading = false
                    this.$refs.patientCheckupAppointments.data = []
                  }

                }

              })
              .finally(() => {
                this.isLoading = false
              })
          })
        },
        headings: {
          start: this.$t('evocall.active_users.started_at'),
          end: this.$t('evocall.active_users.ended_at'),
          status: this.$t('people.appointments_info.status'),
          facility: this.$t('activities_agenda.table.facility')
        }
      },
      tableColumns: ['start', 'end', 'status', 'facility', 'tests'],
      queryUrl: `${OneCareService.baseURL}/appointments/patient/${this.patient.id}?byBegin=true&order_desc=true&isPeopleManager=true`
      //queryUrl: `${OneCareService.baseURL}/appointments/patient/1564?byBegin=true&order_desc=true`,
    }
  },
  methods: {
    handlePatientName: function () {
      var str = ''
      if(this.patient && this.patient.firstName) {
        str += this.patient.firstName
        if (this.patient.lastName) {
          str += ' '+this.patient.lastName
        }
      }
      return str
    },
    handleIconStr: function(measGroupId) {
      const measurementTypeInfo = this.measurementTypesInfo.find(info => info.measurementGroupId === measGroupId)
      if(!measurementTypeInfo) {
        console.log("Cannot find info for meas group id: "+measGroupId)
        return ''
      }
      const iconName = measurementTypeInfo.iconName
      return `i-checkup-${iconName}`
    },
    handleRowClick: function(row) {
      this.selectedAppointment = row
    },
    handleMeasurementDetailsModalClose: function() {
      this.selectedAppointment = null
    },
    distinctMeasurementGroupsInAppointment: function(measurementGroup) {
      const measGroupIds = measurementGroup.map(elem => elem.measurementGroup.idMeasurementGroup)
      return [...new Set(measGroupIds)];
    }
  },
  computed: {
    measurementTypesInfo: function () {
      return this.$store.state.checkup.measurementTypesInfo
    },
    allowedFacilities: function () {
      if(this.allowedEntities.length > 0) {
        return this.allowedEntities.flatMap(entity => entity.facilities)
      }
      return []
    }
  }
};
</script>

<style scoped lang="scss">

</style>
